import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./ProjectDetails.css"; // Import the updated CSS file
import ChecklistPage from "./CheckList";

import { climateZones } from "./climateZones";
import {
  getAllStates,
  getCitiesForState,
  getClimateZone,
  getStateForCity,
  isCityInState,
} from "./stateCityMapping";
import banner3 from "../banner3.png";
import {
  getHotelFullDetails,
  submitHotelCertification,
} from "../../utils/apiService";

const ProjectDetails = () => {
  const certifyingBodyLevels = {
    IGBC: ["Certified", "Silver", "Gold", "Platinum"],
    LEED: ["Certified", "Silver", "Gold", "Platinum"],
    EDGE: ["Certified", "Advanced", "Zero Carbon"],
    GEM: ["GEM 1", "GEM 2", "GEM 3", "GEM 4", "GEM 5"],
    GRIHA: ["1-Star", "2-Star", "3-Star", "4-Star", "5-Star"],
  };

  const handlelevel = (e) => {
    const { name, value } = e.target;

    if (name === "certifyingBody") {
      setFormData((prevData) => ({
        ...prevData,
        certifyingBody: value,
        certificationLevel: "", // Reset certification level when certifying body changes
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const { hotelId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(!!hotelId);
  const [formData, setFormData] = useState({
    projectName: "",
    siteLocation: "",
    siteArea: "",
    climateZone: "",
    airConditionedArea: "",
    employees: "",
    rooms: "",
    builtUpArea: "",
    alreadyCertified: "No",
    certifyingBody: "",
    otherCertifyingBody: "",
    certificationLevel: "",
    CalculatedCertificationLevel: "",
    certificationExpiryDate: "",
    facilityManager: {
      name: "",
      designation: "",
      email: "",
    },
  });

  const [selectedState, setSelectedState] = useState("");
  const [availableCities, setAvailableCities] = useState([]);
  const [checklistData, setChecklistData] = useState(null);

  // Fetch hotel details if hotelId is provided
  useEffect(() => {
    if (hotelId) {
      fetchHotelDetails();
    }
  }, [hotelId]);

  const fetchHotelDetails = async () => {
    setIsLoading(true);
    try {
      const data = await getHotelFullDetails(hotelId);

      // Set form data from API response
      setFormData({
        projectName: data.hotelName || "",
        siteLocation: data.city || "",
        siteArea: data.plotArea || "",
        climateZone: data.climateZone || "",
        airConditionedArea: data.airConditionedArea || "",
        employees: data.numberOfEmployees || "",
        rooms: data.numberOfRooms || "",
        builtUpArea: data.builtUpArea || "",
        alreadyCertified: data.isCurrentlyCertified ? "Yes" : "No",
        certifyingBody: data.certifyingBody || "",
        otherCertifyingBody: data.otherCertifyingBody || "",
        certificationLevel: data.certificationLevel || "",
        CalculatedCertificationLevel: data.CalculatedCertificationLevel || "",
        certificationExpiryDate: data.certificationExpiryDate || "",
        facilityManager: {
          name: data.managerName || "",
          designation: data.managerDesignation || "",
          email: data.managerEmail || "",
        },
      });

      // Set selected state
      setSelectedState(data.state || "");

      // Update available cities based on state
      if (data.state) {
        setAvailableCities(getCitiesForState(data.state));
      }

      // Prepare checklist data for ChecklistPage
      const preparedChecklistData = {
        certificationResponses: data.certificationResponses || [],
        energyData: transformElectricityConsumptions(
          data.electricityConsumptions || []
        ),
        totalPoints: data.totalPoints || 0,
        certificationLevel: data.certificationType || "Not Certified",
      };

      setChecklistData(preparedChecklistData);
    } catch (error) {
      console.error("Error fetching hotel details:", error);
      // Show error notification
      const errorToast = document.createElement("div");
      errorToast.className = "toast-notification error";
      errorToast.innerHTML = `
        <div class="toast-content">
          <i class="fas fa-exclamation-circle"></i>
          <span>Failed to load hotel details. Please try again.</span>
        </div>
      `;
      document.body.appendChild(errorToast);

      // Remove error notification after 3 seconds
      setTimeout(() => {
        errorToast.remove();
        navigate("/dashboard");
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Transform electricity consumption data
  const transformElectricityConsumptions = (consumptions) => {
    // Create a default array with 12 months
    const defaultData = Array(12).fill({
      electricalConsumptionFromUtility: "",
      electricalConsumptionFromOnSiteSolarPV: "",
      electricalConsumptionFromOffSiteRenewable: "",
      totalConsumption: 0,
    });

    // Fill in data from API
    consumptions.forEach((consumption) => {
      const monthIndex = consumption.month - 1; // Convert 1-based to 0-based index
      if (monthIndex >= 0 && monthIndex < 12) {
        defaultData[monthIndex] = {
          electricalConsumptionFromUtility:
            consumption.utilityConsumption.toString(),
          electricalConsumptionFromOnSiteSolarPV:
            consumption.onSiteSolarConsumption.toString(),
          electricalConsumptionFromOffSiteRenewable:
            consumption.offSiteRenewableConsumption.toString(),
          totalConsumption:
            consumption.utilityConsumption +
            consumption.onSiteSolarConsumption +
            consumption.offSiteRenewableConsumption,
        };
      }
    });

    return defaultData;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("facilityManager")) {
      setFormData((prevData) => ({
        ...prevData,
        facilityManager: {
          ...prevData.facilityManager,
          [name.split(".")[1]]: value,
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        // Clear otherCertifyingBody when switching from "Others" to a predefined option
        ...(name === "certifyingBody" &&
          value !== "Others" && { otherCertifyingBody: "" }),
      }));
    }
  };

  const handleSiteLocationChange = (e) => {
    const selectedLocation = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      siteLocation: selectedLocation,
      climateZone: climateZones[selectedLocation] || "",
    }));
  };

  const handleStateChange = (e) => {
    const newState = e.target.value;
    setSelectedState(newState);

    // If the current city doesn't belong to the new state, clear it
    if (
      formData.siteLocation &&
      !isCityInState(formData.siteLocation, newState)
    ) {
      setFormData((prev) => ({
        ...prev,
        siteLocation: "",
        climateZone: "",
      }));
    }
  };

  const handleCityChange = (e) => {
    const newCity = e.target.value;
    const cityState = getStateForCity(newCity);

    setFormData((prev) => ({
      ...prev,
      siteLocation: newCity,
      climateZone: getClimateZone(newCity),
    }));

    // Update state if it's different
    if (cityState && cityState !== selectedState) {
      setSelectedState(cityState);
    }
  };

  // Update available cities when state changes
  useEffect(() => {
    if (selectedState) {
      setAvailableCities(getCitiesForState(selectedState));
    } else {
      setAvailableCities([]);
    }
  }, [selectedState]);

  console.log("formData", formData);
  const handleSubmit = async (checklistData) => {
    try {
      // Format energy consumption data
      const electricityConsumptions = checklistData.energyData.map(
        (row, index) => ({
          year: 2024,
          month: index + 1,
          utilityConsumption:
            parseFloat(row.electricalConsumptionFromUtility) || 0,
          onSiteSolarConsumption:
            parseFloat(row.electricalConsumptionFromOnSiteSolarPV) || 0,
          offSiteRenewableConsumption:
            parseFloat(row.electricalConsumptionFromOffSiteRenewable) || 0,
        })
      );

      // Create the complete API payload
      const apiData = {
        hotelId: hotelId, // Include hotelId for update if available
        hotelName: formData.projectName,
        state: selectedState,
        city: formData.siteLocation,
        plotArea: formData.siteArea,
        climateZone: formData.climateZone,
        airConditionedArea: formData.airConditionedArea,
        numberOfEmployees: formData.employees,
        numberOfRooms: formData.rooms,
        builtUpArea: formData.builtUpArea,
        isCurrentlyCertified: formData.alreadyCertified === "Yes",
        managerName: formData.facilityManager.name,
        managerDesignation: formData.facilityManager.designation,
        managerEmail: formData.facilityManager.email,
        otherCertifyingBody:
          formData.certifyingBody === "Others"
            ? formData.otherCertifyingBody
            : "", // Only include if "Others" is selected
        totalPoints: checklistData.totalPoints,
        certificationResponses: checklistData.certificationResponses,
        electricityConsumptions: electricityConsumptions,
        certifyingBody: formData.certifyingBody,
        certificationLevel:
          formData.certifyingBody === "Others"
            ? ""
            : formData.certificationLevel, // Only include level if not "Others"
        CalculatedCertificationLevel:
          checklistData.CalculatedCertificationLevel,
        ...(formData.alreadyCertified === "Yes" && {
          certificationExpiryDate: formData.certificationExpiryDate || null,
        }),
      };

      // Show loading notification
      const loadingToast = document.createElement("div");
      loadingToast.className = "toast-notification loading";
      loadingToast.innerHTML = `
        <div class="toast-content">
          <div class="spinner"></div>
          <span>${hotelId ? "Updating" : "Submitting"} data...</span>
        </div>
      `;
      document.body.appendChild(loadingToast);

      console.log("apiData", JSON.stringify(apiData));
      const result = await submitHotelCertification(apiData);

      // Remove loading toast
      loadingToast.remove();

      // Show success notification with API response message
      const successToast = document.createElement("div");
      successToast.className = "toast-notification success";
      successToast.innerHTML = `
        <div class="toast-content">
          <i class="fas fa-check-circle"></i>
          <span>${result.message}</span>
        </div>
      `;
      document.body.appendChild(successToast);

      // Remove success notification after 3 seconds
      setTimeout(() => {
        successToast.remove();
        // Redirect to dashboard after successful submission/update
        navigate("/dashboard");
      }, 3000);

      console.log("API Response:", result);
    } catch (error) {
      console.error("Error saving data:", error);

      // Show error notification
      const errorToast = document.createElement("div");
      errorToast.className = "toast-notification error";
      errorToast.innerHTML = `
        <div class="toast-content">
          <i class="fas fa-exclamation-circle"></i>
          <span>Failed to ${
            hotelId ? "update" : "save"
          } data. Please try again.</span>
        </div>
      `;
      document.body.appendChild(errorToast);

      // Remove error notification after 3 seconds
      setTimeout(() => {
        errorToast.remove();
      }, 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading hotel details...</p>
      </div>
    );
  }

  return (
    <div>
      <form className="project-details" onSubmit={(e) => e.preventDefault()}>
        <img src={banner3} alt="Raddison Logo" className="company-logo" />
        <h2 className="project-title">🏢 Hotel Details</h2>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Hotel Name</label>
            <input
              type="text"
              name="projectName"
              value={formData.projectName}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">State</label>
              <select
                className="form-select"
                value={selectedState}
                onChange={handleStateChange}
              >
                <option value="" disabled>
                  Select State
                </option>
                {getAllStates().map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">City</label>
              <select
                className="form-select"
                value={formData.siteLocation}
                onChange={handleCityChange}
                disabled={!selectedState}
              >
                <option value="" disabled>
                  Select City
                </option>
                {availableCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Site/Plot Area(sq ft)
              <i
                className="fa fa-info-circle info-icon"
                title="This refers to the total land area on which the building or project is located. It includes the entire boundary of the plot including the services and green area."
              ></i>
            </label>
            <input
              type="number"
              name="siteArea"
              value={formData.siteArea}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Climate Zone</label>
            <input
              type="text"
              value={formData.climateZone}
              readOnly
              className="form-input"
            />
          </div>
        </div>

        {/* Second Row: Air Conditioned Area, Employees, Rooms, Built-Up Area */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              Air Conditioned Area(sq ft)
              <i
                className="fa fa-info-circle info-icon"
                title="This refers to the portion of the built-up area that is served by an air-conditioning system. It includes all enclosed spaces where cooling is provided, such as offices, living areas, or conference rooms, but excludes non-conditioned spaces like corridors, restrooms, and utility areas."
              ></i>
            </label>
            <input
              type="number"
              name="airConditionedArea"
              value={formData.airConditionedArea}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              No. of Employees(FTE)
              <i
                className="fa fa-info-circle info-icon"
                title="Full-Time Equivalent"
              ></i>
            </label>
            <input
              type="number"
              name="employees"
              value={formData.employees}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">No. of Rooms</label>
            <input
              type="number"
              name="rooms"
              value={formData.rooms}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Built-Up Area <span></span>
              <i
                className="fa fa-info-circle info-icon"
                title="This is the total area covered by all constructed spaces, including all usable covered areas like balconies and utility rooms. It typically excludes open spaces like gardens or courtyards."
              ></i>
            </label>
            <input
              type="number"
              name="builtUpArea"
              placeholder="in Sqft"
              value={formData.builtUpArea}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>

        {/* Already Certified Section */}
        <div className="form-group">
          <label className="form-label">Already Green Certified?</label>
          <div className="radio-group">
            <button
              className={`radio-button ${
                formData.alreadyCertified === "Yes" ? "active" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                const today = new Date().toISOString().split("T")[0];
                setFormData({
                  ...formData,
                  alreadyCertified: "Yes",
                  certificationExpiryDate: today,
                });
              }}
            >
              Yes
            </button>
            <button
              className={`radio-button ${
                formData.alreadyCertified === "No" ? "active" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                setFormData({
                  ...formData,
                  alreadyCertified: "No",
                  certifyingBody: "",
                  certificationLevel: "",
                  certificationExpiryDate: "",
                });
              }}
            >
              No
            </button>
          </div>
        </div>

        {formData.alreadyCertified === "Yes" && (
          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Certifying Body</label>
              <select
                name="certifyingBody"
                value={formData.certifyingBody}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="" disabled selected>
                  Select Certifying Body
                </option>
                <option value="IGBC">IGBC</option>
                <option value="LEED">LEED (USGBC)</option>
                <option value="EDGE">EDGE</option>
                <option value="GEM">ASSOCHAM GEM</option>
                <option value="GRIHA">GRIHA</option>
                <option value="Others">Others</option>
              </select>
              {formData.certifyingBody === "Others" && (
                <input
                  type="text"
                  name="otherCertifyingBody"
                  value={formData.otherCertifyingBody}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Please specify certifying body"
                  style={{ marginTop: "10px" }}
                />
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Level</label>
              <select
                name="certificationLevel"
                value={formData.certificationLevel}
                onChange={handlelevel}
                className="form-select"
                disabled={formData.certifyingBody === "Others"}
                required
              >
                <option value="" disabled selected>
                  Select Level
                </option>
                {certifyingBodyLevels[formData.certifyingBody]?.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Certification Expiry Date</label>
              <input
                type="date"
                name="certificationExpiryDate"
                value={formData.certificationExpiryDate}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>
        )}

        {/* Facility Manager Section */}
        <h3 className="project-title-2">
          {" "}
          <i className="far fa-user-circle"></i> General Manager Details
        </h3>
        <div className="form-row-3">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              type="text"
              name="facilityManager.name"
              value={formData.facilityManager.name}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Contact No.</label>
            <input
              type="text"
              name="facilityManager.designation"
              value={formData.facilityManager.designation}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,10}$/.test(value)) {
                  handleChange(e);
                }
              }}
              placeholder=""
              maxLength={10}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              name="facilityManager.email"
              value={formData.facilityManager.email}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>
      </form>

      <div>
        <ChecklistPage
          selectedClimateZone={formData.climateZone}
          onSave={handleSubmit}
          builtUpArea={formData.builtUpArea}
          initialData={checklistData} // Pass the loaded data to ChecklistPage
          isEditMode={!!hotelId} // Pass whether we're in edit mode
        />
      </div>
    </div>
  );
};

export default ProjectDetails;
